import { useEffect, useMemo, useState } from 'react';
import { TFeatureFlagVariantStatus } from './useFeatureFlagVariantState';
import { usePosthog } from './usePosthog';

/**
 * Tri-state read of a boolean flag. Distinguishes "not resolved yet" from
 * "off", which `useFeatureFlag` cannot: it seeds itself with the default and
 * a consumer that redirects on `false` would do so before PostHog answered.
 *
 * The boolean sibling of `useFeatureFlagVariantState`. Resolves to `disabled`
 * straight away when PostHog was never initialised (no key, or disabled in
 * this build) — there is nothing to wait for and `onFeatureFlags` would never
 * fire.
 */
export const useFeatureFlagState = (flagKey: string): TFeatureFlagVariantStatus => {
  const posthogService = usePosthog();

  const initialStatus = useMemo((): TFeatureFlagVariantStatus => {
    if (!posthogService?.getPosthogInstance()) return 'disabled';
    if (!posthogService.areFlagsReady()) return 'loading';
    return posthogService.isFeatureEnabled(flagKey) ? 'enabled' : 'disabled';
  }, [posthogService, flagKey]);

  const [status, setStatus] = useState<TFeatureFlagVariantStatus>(initialStatus);

  useEffect(() => {
    if (!posthogService?.getPosthogInstance()) {
      setStatus('disabled');
      return;
    }

    const sync = () => {
      setStatus(posthogService.isFeatureEnabled(flagKey) ? 'enabled' : 'disabled');
    };

    // A cache hit resolves now; otherwise the callback fires once flags load
    // (posthog-js fires it on a failed or blocked request too, so no timeout).
    if (posthogService.areFlagsReady()) sync();
    posthogService.onFeatureFlags(sync);
  }, [flagKey, posthogService]);

  return status;
};
